/**
 * Module 6C.4 Layer 1 — Plan badge + usage meter.
 *
 * Rendered in the sidebar footer (Layout.jsx). Shows:
 *   • Plan label (Trial / Free / Studio / Pro / Grandfathered)
 *   • For Trial: days remaining (countdown)
 *   • For paid + free: usage line with leads + projects counts
 *   • Click → /settings (shows the full usage card + upgrade CTAs)
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchBillingUsage, formatLimit, planTone, trialDaysLeft, nudgeCopy,
  openUpgradeProactive,
} from "@/lib/billing";


export default function PlanBadge() {
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await fetchBillingUsage();
      if (!cancelled) setUsage(u);
    })();
    // Refresh whenever a 402 fires — the user just hit a wall and the
    // sidebar usage line should match what the modal will say next.
    const onUpgrade = async () => {
      const u = await fetchBillingUsage();
      if (!cancelled) setUsage(u);
    };
    window.addEventListener("lead_studio:upgrade_required", onUpgrade);
    window.addEventListener("lead_studio:plan_changed", onUpgrade);
    return () => {
      cancelled = true;
      window.removeEventListener("lead_studio:upgrade_required", onUpgrade);
      window.removeEventListener("lead_studio:plan_changed", onUpgrade);
    };
  }, []);

  if (!usage) return null;

  const tone = planTone(usage.plan);
  const days = trialDaysLeft(usage);
  const showUsageLine = usage.plan === "free" || usage.plan === "studio" || usage.plan === "pro";
  const nudge = nudgeCopy(usage);
  // Module 6C.4 Layer 2.1 — proactive upgrade pill only for the two
  // eligible plans. Paid plans see plan-management via /settings#billing;
  // grandfathered studios don't need the CTA.
  const showUpgradePill = usage.plan === "trial" || usage.plan === "free";

  return (
    <div
      className="w-full flex flex-col items-start gap-1 px-3 py-2 text-left"
      style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
      data-testid="plan-badge"
    >
      <button
        onClick={() => navigate("/settings#billing")}
        className="w-full flex items-center gap-2 hover:opacity-90 text-left"
        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
        data-testid="plan-badge-open"
      >
        <span
          className="text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5"
          style={{ background: tone.bg, color: tone.fg, borderRadius: 9999 }}
          data-testid="plan-badge-label"
        >
          {usage.label}
        </span>
        {usage.plan === "trial" && days !== null && (
          <span className="text-[10px] tracking-wide text-brand-paper-soft/70" data-testid="plan-trial-days">
            {days}d left
          </span>
        )}
      </button>
      {showUsageLine && (
        <button
          onClick={() => navigate("/settings#billing")}
          className="text-[10px] text-brand-paper-soft/60 leading-tight text-left hover:opacity-90"
          style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
          data-testid="plan-usage-line"
        >
          {formatLimit(usage.leads.used, usage.leads.limit, usage.leads.window)} · leads<br />
          {formatLimit(usage.projects.used, usage.projects.limit, usage.projects.window)} · projects
        </button>
      )}
      {/* Trial nudges fire on day 10 (soft) and day 13 (critical). The
          backend lazy-computes which to show; we just render. */}
      {nudge && (
        <button
          onClick={() => navigate("/settings#billing")}
          className={`text-[10px] leading-tight px-1.5 py-1 mt-1 w-full text-left hover:opacity-90 ${nudge.tone === "critical" ? "text-white" : "text-brand-ink"}`}
          style={{
            background: nudge.tone === "critical" ? "var(--brand-primary)" : "var(--brand-paper-soft)",
            borderRadius: 4, border: "none", cursor: "pointer",
          }}
          data-testid={`plan-nudge-${nudge.tone}`}
        >
          {nudge.text}
        </button>
      )}
      {showUpgradePill && (
        <button
          onClick={() => openUpgradeProactive({ currentPlan: usage.plan, label: usage.label })}
          className="mt-1 text-[9px] tracking-[0.18em] uppercase px-2 py-1 text-white hover:opacity-90"
          style={{
            background: "var(--brand-primary)",
            borderRadius: 4, border: "none", cursor: "pointer",
            width: "100%",
          }}
          data-testid="sidebar-upgrade-pill"
        >
          Upgrade →
        </button>
      )}
    </div>
  );
}
